import { Link } from "react-router-dom";

type PriceRow = {
  name: string;
  price: string;
  data: string;
  note: string;
};

function PriceTable({ rows }: { rows: PriceRow[] }) {
  return (
    <div className="not-prose my-7 overflow-x-auto border-y-2 border-foreground-900 bg-white">
      <table className="w-full min-w-[680px] border-collapse text-left text-sm">
        <thead className="bg-background-100 text-foreground-900">
          <tr><th className="p-3">サービス</th><th className="p-3">月額</th><th className="p-3">データ量</th><th className="p-3">確認ポイント</th></tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.name} className="border-t border-background-300">
              <th className="p-3 font-bold text-foreground-900">{row.name}</th>
              <td className="p-3 font-bold text-primary-700">{row.price}</td>
              <td className="p-3">{row.data}</td>
              <td className="p-3 leading-6 text-foreground-700">{row.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function WhichPlanIsCheapestArticle() {
  return (
    <>
      <section id="intro">
        <p className="text-[1.08em] leading-[1.9] text-foreground-900">
          「で、結局どこが安いの？」。この質問に、会社名だけで答えると失敗します。1GBしか使わない人と、毎日動画を見る人では、最安候補が違うからです。
        </p>
        <p>この記事では、使用量の目安ごとに、候補となるプランの通常基本料金を紹介します。各プランの容量・通話条件は異なり、同一使用量での最安順位ではありません。端末代、期間限定キャンペーン、ポイント還元は分けて考えます。</p>
        <aside><strong>先に結論</strong><p>料金だけならオンライン専用プランやMVNOが強い一方、家族割・自宅回線・店舗サポートまで含めると、大手やサブブランドが合う世帯もあります。</p></aside>
      </section>

      <section id="quick-pick">
        <h2>先に、自分に近い使い方から読む</h2>
        <p>すべての料金表を読む必要はありません。直近3か月の使用量と、外せない条件に近い入口を選んでください。</p>
        <div className="not-prose my-7 grid gap-3 sm:grid-cols-2">
          <a href="#light-user" className="border-y-2 border-foreground-900 bg-white px-5 py-5 no-underline">
            <small className="text-xs font-bold text-primary-600">家ではWi-Fi中心</small>
            <strong className="mt-1 block text-base text-foreground-900">月3GB前後の安い候補を見る</strong>
            <span className="mt-2 block text-xs leading-6 text-foreground-600">日本通信SIM・IIJmio・LINEMO・楽天モバイル</span>
          </a>
          <a href="#medium-user" className="border-y-2 border-foreground-900 bg-white px-5 py-5 no-underline">
            <small className="text-xs font-bold text-primary-600">SNS・地図・動画を普通に使う</small>
            <strong className="mt-1 block text-base text-foreground-900">月15〜20GBの候補を見る</strong>
            <span className="mt-2 block text-xs leading-6 text-foreground-600">料金に加えて昼休みの速度や通話も確認</span>
          </a>
          <a href="#heavy-user" className="border-y-2 border-foreground-900 bg-white px-5 py-5 no-underline">
            <small className="text-xs font-bold text-primary-600">外でも動画をよく見る</small>
            <strong className="mt-1 block text-base text-foreground-900">月30〜40GBの候補を見る</strong>
            <span className="mt-2 block text-xs leading-6 text-foreground-600">ahamo・LINEMO・UQ・Y!mobileなどを比較</span>
          </a>
          <a href="#unlimited-user" className="border-y-2 border-foreground-900 bg-white px-5 py-5 no-underline">
            <small className="text-xs font-bold text-primary-600">動画・テザリング中心</small>
            <strong className="mt-1 block text-base text-foreground-900">無制限の候補を見る</strong>
            <span className="mt-2 block text-xs leading-6 text-foreground-600">楽天モバイルと大手3社の料金・制限を確認</span>
          </a>
        </div>
      </section>

      <section id="four-layers">
        <h2>いまの携帯料金は「4つの種類」に分けて考える</h2>
        <p>まず全体の地図から。いまの日本の携帯料金は、サービスの特徴によって大きく4つに分けられます。</p>
        <div className="not-prose my-7 border-y-2 border-foreground-900 bg-white">
          {[
            ["1. 大手キャリア", "ドコモ・au・ソフトバンク。フルサービスで、店舗、家族割、光セット割がある。"],
            ["2. 楽天モバイル", "第4のキャリア。使ったデータ量に応じて料金が変わる、シンプルな一本型プラン。"],
            ["3. サブブランド・オンライン専用", "UQ mobile・Y!mobile／ahamo・povo・LINEMO。大手の回線品質を、店舗少なめ・オンライン中心で安く利用する選択肢。"],
            ["4. 格安SIM（MVNO）", "IIJmio・mineo・日本通信SIMなど。料金を抑えやすい最安ゾーン。"],
          ].map(([title, text], index) => (
            <div key={title} className={`grid gap-2 px-2 py-5 md:grid-cols-[190px_1fr] md:gap-7 md:px-4 ${index ? "border-t border-background-300" : ""}`}>
              <strong className="text-[16px] leading-7 text-foreground-900">{title}</strong>
              <p className="m-0 text-[15px] leading-7 text-foreground-700">{text}</p>
            </div>
          ))}
        </div>
        <p>同じ「スマホが使える」サービスでも、種類によって料金やサポートは大きく違います。ここから、自分の使い方に近い容量帯で比較します。</p>
      </section>

      <section id="light-user">
        <h2>ほとんど使わない人｜月3GB前後・家ではWi-Fi</h2>
        <PriceTable rows={[
          { name: "日本通信SIM", price: "290円", data: "1GB", note: "3GB使う場合は730円（290円＋追加2GB×220円の計算値）。通話料・初期費用などは別。" },
          { name: "IIJmio", price: "950円", data: "5GB", note: "音声SIM。小容量でも少し余裕を持たせたい人向け。" },
          { name: "LINEMO", price: "990円", data: "3GBまで", note: "3GB超〜10GBは2,090円。手続きはオンライン中心。" },
          { name: "楽天モバイル", price: "1,078円", data: "3GBまで", note: "使用量で料金が変わる。利用場所の電波状況も確認。" },
        ]} />
        <p>この帯は、割引なしの大手小容量料金との差が出やすいゾーンです。ただし、家族割や自宅回線割が大きい世帯では、自分の1回線だけを動かすと家族側の料金が上がることがあります。</p>
      </section>

      <section id="medium-user">
        <h2>ふつうに使う人｜月15〜20GB前後</h2>
        <PriceTable rows={[
          { name: "日本通信SIM", price: "1,390円", data: "20GB", note: "5分かけ放題または月70分の無料通話を選べる。" },
          { name: "IIJmio", price: "1,600円", data: "15GB", note: "通話定額は別。必要容量を細かく選びたい人向け。" },
          { name: "mineo", price: "1,958円", data: "15GB", note: "余った容量の繰り越しやパケット放題の条件を確認。" },
          { name: "楽天モバイル", price: "2,178円", data: "3GB超〜20GB", note: "Rakuten Linkの通話条件と利用場所の相性を確認。" },
        ]} />
        <p>この容量帯は1,000円台から候補があります。ただし15GBプランの料金で20GBを高速通信できるわけではありません。必要容量や超過時の追加料金を確認し、昼休みなど混雑時間帯の使い方、通話、初期設定を自分で進められるかも比べます。</p>
      </section>

      <section id="heavy-user">
        <h2>たっぷり使う人｜月30〜40GB前後</h2>
        <PriceTable rows={[
          { name: "mineo", price: "2,178円", data: "30GB", note: "音声通話付き。パケット放題は別途申込み条件あり。" },
          { name: "LINEMO", price: "2,970円", data: "30GB", note: "1回5分以内の国内通話定額を含む。" },
          { name: "ahamo", price: "2,970円", data: "40GB※", note: "通常30GBに10GBを増量するキャンペーン中。終了後は通常容量へ戻る。" },
          { name: "UQ mobile", price: "3,828円", data: "35GB", note: "1回10分以内の国内通話とPontaパスを含む。" },
          { name: "Y!mobile", price: "4,378円", data: "30GB", note: "割引前。家族割または自宅回線割、カード割で変わる。" },
        ]} />
        <p>※ahamoの40GB増量は2026年8月1日〜11月30日です。2026年10月5日に公式FAQを再確認。12月1日からサービス仕様・料金の改定が予告されているため、今後の条件は公式案内をご確認ください。</p>
        <p>30GB前後では、通話込みか、店舗を使えるか、家族・自宅回線の割引があるかで差が縮みます。「月額が安い順」だけではなく、必要なサービスを足した後の金額で比べます。</p>
      </section>

      <section id="unlimited-user">
        <h2>無制限を使いたい人｜動画・テザリング中心</h2>
        <PriceTable rows={[
          { name: "楽天モバイル", price: "3,278円", data: "無制限※", note: "利用場所の電波状況と、混雑時などの制御条件を確認。" },
          { name: "au", price: "7,788円", data: "使い放題※", note: "割引フル適用例は5,258円。テザリング等は合計60GBまで。" },
          { name: "SoftBank", price: "8,008円", data: "無制限※", note: "テイガク無制限。割引フル適用例は5,148円。" },
          { name: "ドコモ", price: "8,448円", data: "無制限※", note: "ドコモ MAX。各種割引適用例は5,148円。" },
        ]} />
        <p>※各社とも大量通信、混雑、テザリング等に個別条件があります。完全に無条件の無制限という意味ではありません。</p>
        <p>楽天モバイルと大手3社の割引前料金には月4,500〜5,000円前後の差があります。一方、大手の割引を最大まで使える場合は差が約2,000円まで縮まります。自宅回線の料金やカード年会費まで含めて世帯総額で見ます。</p>
      </section>

      <section id="cheap-caveats">
        <h2>月額の安さには、確認したい条件がある</h2>
        <ul>
          <li><strong>大手：</strong>割引前だけで比べると不利。家族回線、自宅ネット、カード、付帯サービスを含める。</li>
          <li><strong>オンライン専用：</strong>申込み、初期設定、トラブル確認を自分で進める場面が多い。</li>
          <li><strong>MVNO：</strong>混雑時間帯の速度、端末設定、店舗サポートの範囲を確認する。</li>
          <li><strong>楽天モバイル：</strong>生活圏・建物内・通勤経路での電波との相性を確認する。</li>
        </ul>
        <aside><strong>最安より「困ったときに戻れるか」も大切。</strong><p>家族に設定を頼める、デュアルSIMで試せる、店舗で相談できるなど、失敗したときの戻りやすさも選択条件です。</p></aside>
      </section>

      <section id="four-steps">
        <h2>選ぶ順番は、この4つだけ</h2>
        <ol>
          <li><strong>過去3か月のデータ使用量：</strong>契約容量ではなく、実際に使った量を見る。</li>
          <li><strong>電話の長さと回数：</strong>5分、10分、完全かけ放題のどれが必要か確認する。</li>
          <li><strong>店舗サポート：</strong>自分だけでなく、家族に店頭相談が必要な人がいるか数える。</li>
          <li><strong>割引のつながり：</strong>家族割・光回線・カード割が、誰かの乗り換えで外れないか確認する。</li>
        </ol>
        <p>この4つを確認してから、自分の使用量の表へ戻ります。1回線の月額ではなく、世帯全体で下がるかを判断してください。</p>
      </section>

      <section id="expert-view">
        <h2>通信業界の現場経験から伝えたいこと</h2>
        <p>安い会社を知ることより、自分の条件をそろえて比べることの方が重要です。店舗での相談、家族の設定支援、自宅回線との割引に価値がある人もいれば、オンラインで完結できるので月額を下げたい人もいます。</p>
        <p>「格安SIMだから正解」「大手だから損」と一律には決めません。料金、端末、サポート、家族への影響を同じ紙に書けば、自分に合う候補が見えます。</p>
      </section>

      <section id="sources">
        <h2>公式情報と確認日</h2>
        <p>料金は税込、2026年9月20日に各社公式情報を確認。端末代、通話料、初期費用、期間限定キャンペーンは特記がない限り含みません。</p>
        <ul>
          <li><a href="https://www.nihontsushin.com/entry/index.html" target="_blank" rel="noreferrer">日本通信SIM 料金プラン ↗</a></li>
          <li><a href="https://www.iijmio.jp/gigaplan/" target="_blank" rel="noreferrer">IIJmio ギガプラン ↗</a></li>
          <li><a href="https://mineo.jp/price/" target="_blank" rel="noreferrer">mineo 料金表 ↗</a></li>
          <li><a href="https://www.linemo.jp/plan/index_b.html" target="_blank" rel="noreferrer">LINEMO 料金プラン ↗</a></li>
          <li><a href="https://faq.ahamo.com/faq/show/4124?site_domain=default" target="_blank" rel="noreferrer">ahamo データ増量おためしキャンペーン ↗</a></li>
          <li><a href="https://www.uqwimax.jp/mobile/plan/komikomi-plan-value/" target="_blank" rel="noreferrer">UQ mobile コミコミプランバリュー ↗</a></li>
          <li><a href="https://www.ymobile.jp/plan/" target="_blank" rel="noreferrer">Y!mobile 料金プラン ↗</a></li>
          <li><a href="https://network.mobile.rakuten.co.jp/fee/saikyo-plan/detail/" target="_blank" rel="noreferrer">楽天モバイル Rakuten最強プラン ↗</a></li>
          <li><a href="https://www.docomo.ne.jp/charge/docomo_max/" target="_blank" rel="noreferrer">ドコモ MAX ↗</a></li>
          <li><a href="https://www.au.com/mobile/charge/smartphone/plan/data-unlimited-plus/" target="_blank" rel="noreferrer">au 使い放題MAX＋ ↗</a></li>
          <li><a href="https://www.softbank.jp/mobile/price_plan/data/teigaku-museigen/" target="_blank" rel="noreferrer">SoftBank テイガク無制限 ↗</a></li>
        </ul>
      </section>

      <section id="next-actions">
        <h2>自分の条件で確かめる</h2>
        <p>まず請求を4つに分け、データ使用量と通話を確認します。条件が分かれば、料金表を自分向けに絞れます。</p>
        <div>
          <Link to="/bill-check"><small>いまの支払いを整理</small><strong>請求内訳ツールを使う</strong><span>→</span></Link>
          <Link to="/diagnosis"><small>使い方から候補を絞る</small><strong>料金診断へ進む</strong><span>→</span></Link>
        </div>
      </section>
    </>
  );
}
